import { Grid, Card, CardHeader, CardContent, Table, TableHead, TableRow, TableCell, TableBody, Chip } from '@mui/material';
import { useQuery } from '@tanstack/react-query';
import { PageHeader } from '../../components/PageHeader';
import { StatCard } from '../../components/StatCard';
import { MiniBarChart } from '../../components/MiniBarChart';
import { EmptyState } from '../../components/EmptyState';
import { integrationLogApi } from '../../api/integration.api';

const n = (x = 0) => Number(x).toLocaleString('en-IN');

function ChartCard({ title, data, color = '#1e40af' }: { title: string; data: Array<{ label: string; value: number }>; color?: string }) {
  return (
    <Card sx={{ height: '100%' }}>
      <CardHeader title={title} titleTypographyProps={{ variant: 'subtitle1' }} />
      <CardContent>{data.length ? <MiniBarChart data={data.map((d) => ({ ...d, color }))} /> : <EmptyState title="No data yet" dense />}</CardContent>
    </Card>
  );
}

export function MonitoringPage() {
  const { data } = useQuery({ queryKey: ['integration-monitoring'], queryFn: () => integrationLogApi.monitoring(), refetchInterval: 15000 });
  const k = data?.kpis;
  const c = data?.charts;

  return (
    <>
      <PageHeader title="API Monitoring Dashboard" subtitle="Live traffic trends, latency bands, status codes and recent failures (auto-refreshes)." />
      <Grid container spacing={2} mb={1}>
        <Grid item xs={12} sm={4}><StatCard gradient color="#1e40af" label="Total API Calls" value={n(k?.totalCalls)} /></Grid>
        <Grid item xs={12} sm={4}><StatCard gradient color="#16a34a" label="Success Rate" value={`${k?.successRate ?? 100}%`} /></Grid>
        <Grid item xs={12} sm={4}><StatCard gradient color="#dc2626" label="Errors" value={n(k?.errors)} /></Grid>
      </Grid>
      <Grid container spacing={2}>
        <Grid item xs={12} md={6}><ChartCard title="Call Volume (by day)" data={c?.volumeTrend ?? []} /></Grid>
        <Grid item xs={12} md={6}><ChartCard title="Errors (by day)" data={c?.errorTrend ?? []} color="#dc2626" /></Grid>
        <Grid item xs={12} md={4}><ChartCard title="HTTP Status Codes" data={c?.statusCodes ?? []} color="#7c3aed" /></Grid>
        <Grid item xs={12} md={4}><ChartCard title="Latency Distribution" data={c?.latency ?? []} color="#d97706" /></Grid>
        <Grid item xs={12} md={4}><ChartCard title="Top Integrations" data={c?.topIntegrations ?? []} color="#0d9488" /></Grid>
        <Grid item xs={12}>
          <Card>
            <CardHeader title="Recent Errors" titleTypographyProps={{ variant: 'subtitle1' }} />
            <CardContent sx={{ p: 0, overflowX: 'auto' }}>
              {(data?.recentErrors.length ?? 0) === 0 ? <EmptyState title="No recent errors" dense /> : (
                <Table size="small">
                  <TableHead><TableRow><TableCell>Integration</TableCell><TableCell>Direction</TableCell><TableCell>Event</TableCell><TableCell>HTTP</TableCell><TableCell>Error</TableCell></TableRow></TableHead>
                  <TableBody>
                    {data?.recentErrors.map((e) => (
                      <TableRow key={e.id} hover>
                        <TableCell>{e.integration ?? '—'}</TableCell>
                        <TableCell><Chip size="small" label={e.direction} /></TableCell>
                        <TableCell>{e.event ?? '—'}</TableCell>
                        <TableCell>{e.responseStatus ?? '—'}</TableCell>
                        <TableCell sx={{ maxWidth: 320, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{e.error ?? '—'}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </>
  );
}
